import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

const FlightBookingDetails = ({ 
  flightData = {
    aircraft: 'SASA',
    departure: 'LCG',
    departureTime: '19:05 PM',
    arrival: 'AAL',
    arrivalTime: '18:30 PM',
    duration: '3 hrs 30 Mins',
    seats: 4,
    flightNumber: 'SASA',
    etd: '17 Feb 2026, 19:05',
    eta: '19 Feb 2026, 18:30',
    flightTime: '3 hrs 30 Mins',
    totalFlightTime: '3 hrs 30 Mins',
    addOns: '$5',
    totalFare: '$185',
    image: '/hero-image.jpeg'
  },
  carouselImages = [
    '/hero-image.jpeg',
    '/gallery1.jpeg',
    '/gallery3.jpeg',
    '/gallery4.jpeg',
    '/gallery5.jpeg'
  ],
  onBook = () => {}
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [carouselImages.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);
  };

  return (
    <div className="w-full bg-white">
      {/* Carousel Section */}
      <div className="relative w-full h-96 md:h-[500px] overflow-hidden bg-gray-200">
        {carouselImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img 
              src={image} 
              alt={`Flight ${index + 1}`}
              className="w-full h-full object-cover"
            />
          </div>
        ))}

        {/* Carousel Controls */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
          aria-label="Previous slide"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="w-5 h-5" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
          aria-label="Next slide"
        >
          <FontAwesomeIcon icon={faChevronRight} className="w-5 h-5" />
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10 flex gap-2">
          {carouselImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentSlide ? 'bg-white' : 'bg-white/50'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Flight Details Section */}
      <div className="px-4 md:px-8 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Flight Header */}
          <div className="flex flex-col md:flex-row gap-6 mb-8 pb-8 border-b border-gray-200">
            {/* Aircraft Image */}
            <div className="md:w-1/4">
              <img 
                src={flightData.image} 
                alt="Aircraft"
                className="w-full h-32 object-cover rounded-lg"
              />
            </div>

            {/* Left Column - Aircraft Info */}
            <div className="md:w-1/4 text-sm">
              <p className="font-semibold text-gray-900 text-base mb-3">{flightData.aircraft}</p>
              <p className="text-gray-600 text-xs mb-2">LED 8:03 PM</p>
              <p className="text-gray-600 text-xs mb-2">Fly to {flightData.arrival}</p>
              <p className="text-gray-600 text-xs mb-2">Seats Available: {flightData.seats}</p>
              <p className="text-gray-600 font-semibold text-xs">Fare/Per: $50</p>
            </div>

            {/* Center Column - Flight Route Info */}
            <div className="md:w-1/4 flex flex-col justify-center">
              <div className="text-center">
                <p className="text-xs text-gray-600 mb-1">{flightData.departure}</p>
                <p className="text-xl font-bold text-gray-900 mb-3">{flightData.departureTime}</p>
                <div className="flex items-center justify-center gap-2 my-3">
                  <div className="flex-1 h-px bg-gray-300"></div>
                  <span className="text-xs text-gray-600 px-2 whitespace-nowrap">{flightData.duration}</span>
                  <div className="flex-1 h-px bg-gray-300"></div>
                </div>
                <p className="text-xs text-gray-600 mb-1">{flightData.arrival}</p>
                <p className="text-xl font-bold text-gray-900">{flightData.arrivalTime}</p>
              </div>
            </div>

            {/* Right Column - Additional Info */}
            <div className="md:w-1/4 flex flex-col justify-center text-right">
              <p className="text-xs text-gray-600 mb-1">TAB 18:30 PM</p>
              <p className="text-xs text-gray-600 mb-4">Trip 19 Feb 26</p>
              <button className="text-yellow-500 hover:text-yellow-600 font-semibold text-xs">
                Before You Book ▼
              </button>
            </div>
          </div>

          {/* Flight Summary Section */}
          <div className="bg-white mb-8">
            {/* Yellow Header */}
            <div className="bg-yellow-400 px-6 py-3 mb-0">
              <h3 className="text-base font-bold text-gray-900">Flight Summary</h3>
            </div>

            {/* Table Content */}
            <div className="border border-gray-200 border-t-0">
              {/* First Row - Flight Details */}
              <div className="grid grid-cols-6 gap-4 px-6 py-4 border-b border-dashed border-gray-500">
                <div>
                  <p className="text-sm text-gray-900 font-semibold mb-1">Flt. No.</p>
                  <p className="text-sm text-gray-900">{flightData.flightNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-900 font-semibold mb-1">Dep.</p>
                  <p className="text-sm text-gray-900">{flightData.departure}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-900 font-semibold mb-1">ETD</p>
                  <p className="text-sm text-gray-900">{flightData.etd}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-900 font-semibold mb-1">Arr.</p>
                  <p className="text-sm text-gray-900">{flightData.arrival}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-900 font-semibold mb-1">ETA</p>
                  <p className="text-sm text-gray-900">{flightData.eta}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-900 font-semibold mb-1">Flight Time</p>
                  <p className="text-sm text-gray-900">{flightData.flightTime}</p>
                </div>
              </div>

              {/* Fare Header Row */}
              <div className="grid grid-cols-2 gap-4 px-6 py-3 border-b border-dashed border-gray-500">
                <div>
                  <p className="text-sm text-gray-900 font-semibold">Fare</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-900 font-semibold">Amount</p>
                </div>
              </div>

              {/* Total Flight Time Row */}
              <div className="grid grid-cols-2 gap-4 px-6 py-3 border-b border-dashed border-gray-500">
                <div>
                  <p className="text-sm text-gray-700">Total Flight Time</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-900 font-semibold">{flightData.totalFlightTime}</p>
                </div>
              </div>

              {/* Add-ons Row */}
              <div className="grid grid-cols-2 gap-4 px-6 py-3 border-b border-dashed border-gray-500">
                <div>
                  <p className="text-sm text-gray-700">Add-ons</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-900 font-semibold">{flightData.addOns}</p>
                </div>
              </div>

              {/* Total Fare Row */}
              <div className="grid grid-cols-2 gap-4 px-6 py-4 border-b border-dashed border-gray-500">
                <div>
                  <p className="text-sm text-gray-900 font-bold">Total Fare</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-900 font-bold">{flightData.totalFare}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Book Button */}
          <div className="flex justify-end">
            <button
              onClick={onBook}
              className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold py-2 px-6 rounded text-sm transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlightBookingDetails;
